import type { DailySalesDay, SalesTotals } from '../../../../api/daily-sales';
import {
  dayTotal,
  formatCount,
  formatMoney,
  shortDate,
  totalsOf,
  weekdayName,
  windowTotal,
  type SourceKey,
} from '../daily-sales-format';

interface LedgerRow {
  key: string;
  label: string;
  partial: boolean;
  cells: string[];
}

function cellsFor(day: DailySalesDay): string[] {
  const fh = totalsOf(day, 'fareharbor');
  const gc = totalsOf(day, 'giftCards');
  const nb = day.nebari;
  return [
    formatCount(fh.bookings),
    formatCount(fh.tickets),
    formatMoney(fh.grossCents),
    formatCount(gc.tickets),
    formatMoney(gc.grossCents),
    formatCount(nb.bookings),
    formatCount(nb.tickets),
    formatMoney(nb.grossCents),
    formatCount(dayTotal(day, 'bookings')),
    formatMoney(dayTotal(day, 'grossCents')),
  ];
}

function rowFor(day: DailySalesDay, today: string): LedgerRow {
  const partial = day.date === today;
  return {
    key: day.date,
    label: `${weekdayName(day.date)} ${shortDate(day.date)}${partial ? ' (so far)' : ''}`,
    partial,
    cells: cellsFor(day),
  };
}

function totalRow(days: DailySalesDay[]): LedgerRow {
  const sum = (source: SourceKey): SalesTotals => ({
    bookings: windowTotal(days, source, 'bookings'),
    tickets: windowTotal(days, source, 'tickets'),
    grossCents: windowTotal(days, source, 'grossCents'),
    subtotalCents: windowTotal(days, source, 'subtotalCents'),
  });
  const window: DailySalesDay = {
    date: 'total',
    fareharbor: sum('fareharbor'),
    giftCards: sum('giftCards'),
    nebari: sum('nebari'),
  };
  return { key: 'total', label: 'Total', partial: false, cells: cellsFor(window) };
}

const GROUPS = [
  { name: 'FareHarbor', span: 3 },
  { name: 'Gift cards', span: 2 },
  { name: 'Nebari', span: 3 },
  { name: 'All', span: 2 },
];
const COLUMNS = [
  'Bookings',
  'Tickets',
  'Gross',
  'Sold',
  'Gross',
  'Bookings',
  'Tickets',
  'Gross',
  'Bookings',
  'Gross',
];

export function DailySalesLedger({ days, today }: { days: DailySalesDay[]; today: string }) {
  const rows = [...days].reverse().map((d) => rowFor(d, today));
  const total = totalRow(days);

  return (
    <section className="nb-card nb-ds-ledger" aria-label="Sales by day">
      <table className="nb-tbl nb-tabular">
        <thead>
          <tr>
            <th rowSpan={2} scope="col">
              Day
            </th>
            {GROUPS.map((g) => (
              <th key={g.name} colSpan={g.span} scope="colgroup" className="nb-ds-group">
                {g.name}
              </th>
            ))}
          </tr>
          <tr>
            {COLUMNS.map((c, i) => (
              <th key={i} scope="col">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={r.partial ? 'nb-ds-partial' : undefined}>
              <th scope="row">{r.label}</th>
              {r.cells.map((c, i) => (
                <td key={i}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="nb-ds-total">
            <th scope="row">{total.label}</th>
            {total.cells.map((c, i) => (
              <td key={i}>{c}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </section>
  );
}
